/**
 * @package FlorenceEGI — EGI — Home Supreme FEPlate SVG generator
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Deterministic SVG placeholders (Rinascimento tones). Used as
 *          thumbnail fallback when `thumb` is null. Zero raster deps,
 *          server-agnostic. Ported from /tmp/florenceegi-home-sources/FEPlate.
 */

export type FEPlateTone = "nero" | "travertino" | "lauro" | "oro";

type Palette = { bg: string; fg: string; accent: string; line: string };

const PALETTES: Record<FEPlateTone, Palette> = {
    nero:       { bg: "#121110", fg: "#E7DDBF", accent: "#D4AF37", line: "rgba(212,175,55,0.18)" },
    travertino: { bg: "#EDE6D3", fg: "#3B342A", accent: "#A58A2A", line: "rgba(60,50,30,0.18)" },
    lauro:      { bg: "#1F2F26", fg: "#D9C87E", accent: "#D4AF37", line: "rgba(212,175,55,0.22)" },
    oro:        { bg: "#2A2416", fg: "#EBD99A", accent: "#D4AF37", line: "rgba(235,217,154,0.22)" },
};

export function feHash(str: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
}

const SVG_NS = "http://www.w3.org/2000/svg";

function setAttrs(node: SVGElement, attrs: Record<string, string | number>): void {
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
}

function svg<K extends keyof SVGElementTagNameMap>(
    tag: K,
    attrs?: Record<string, string | number>,
): SVGElementTagNameMap[K] {
    const node = document.createElementNS(SVG_NS, tag);
    if (attrs) setAttrs(node, attrs);
    return node;
}

export function buildPlate(seed: string, tone: FEPlateTone = "nero"): SVGSVGElement {
    const p = PALETTES[tone] ?? PALETTES.nero;
    const h = feHash(seed);
    const rotate = ((h % 24) - 12) * 0.7;
    const cx = 30 + (h % 40);
    const cy = 30 + ((h >>> 4) % 40);
    const ringR = 120 + ((h >>> 8) % 120);
    const ringR2 = 60 + ((h >>> 12) % 60);
    const shape = h % 5;
    const gid = `fe-rg-${(h >>> 0).toString(36)}`;
    const pid = `fe-ls-${(h >>> 0).toString(36)}`;

    const root = svg("svg", {
        viewBox: "0 0 400 600",
        preserveAspectRatio: "xMidYMid slice",
        "aria-hidden": "true",
        focusable: "false",
    });
    root.style.background = p.bg;

    const defs = svg("defs");
    const rg = svg("radialGradient", { id: gid, cx: `${cx}%`, cy: `${cy}%`, r: "70%" });
    const s1 = svg("stop", { offset: "0%", "stop-color": p.accent, "stop-opacity": "0.28" });
    const s2 = svg("stop", { offset: "55%", "stop-color": p.accent, "stop-opacity": "0.05" });
    const s3 = svg("stop", { offset: "100%", "stop-color": p.bg, "stop-opacity": "0" });
    rg.append(s1, s2, s3);
    const pat = svg("pattern", {
        id: pid, width: 6, height: 6,
        patternUnits: "userSpaceOnUse",
        patternTransform: `rotate(${rotate + 30})`,
    });
    pat.append(svg("line", { x1: 0, y1: 0, x2: 0, y2: 6, stroke: p.line, "stroke-width": "0.6" }));
    defs.append(rg, pat);
    root.appendChild(defs);

    root.append(
        svg("rect", { width: 400, height: 600, fill: p.bg }),
        svg("rect", { width: 400, height: 600, fill: `url(#${gid})` }),
        svg("rect", { width: 400, height: 600, fill: `url(#${pid})`, opacity: "0.6" }),
    );

    const cxPx = cx * 4;
    const cyPx = cy * 6;

    if (shape === 0) {
        root.append(
            svg("circle", { cx: cxPx, cy: cyPx, r: ringR, fill: "none", stroke: p.accent, "stroke-opacity": "0.22", "stroke-width": "0.8" }),
            svg("circle", { cx: cxPx, cy: cyPx, r: ringR2, fill: "none", stroke: p.accent, "stroke-opacity": "0.35", "stroke-width": "0.6" }),
        );
    } else if (shape === 1) {
        const g = svg("g", { transform: `translate(${cxPx} ${cyPx}) rotate(${rotate})` });
        g.append(
            svg("rect", { x: -ringR / 2, y: -ringR, width: ringR, height: ringR * 2, fill: "none", stroke: p.accent, "stroke-opacity": "0.22", "stroke-width": "0.8" }),
            svg("line", { x1: -ringR / 2, y1: 0, x2: ringR / 2, y2: 0, stroke: p.accent, "stroke-opacity": "0.4", "stroke-width": "0.6" }),
        );
        root.appendChild(g);
    } else if (shape === 2) {
        const g = svg("g", { opacity: "0.35" });
        const dy = rotate * 5;
        const dy2 = rotate * 3;
        g.append(
            svg("path", { d: `M 40 ${300 + dy} Q 200 ${200 + dy2}, 360 ${330 + dy}`, fill: "none", stroke: p.accent, "stroke-width": "0.8" }),
            svg("path", { d: `M 40 ${340 + dy} Q 200 ${260 + dy2}, 360 ${380 + dy}`, fill: "none", stroke: p.accent, "stroke-width": "0.5" }),
            svg("path", { d: `M 40 ${380 + dy} Q 200 ${310 + dy2}, 360 ${430 + dy}`, fill: "none", stroke: p.accent, "stroke-width": "0.3" }),
        );
        root.appendChild(g);
    } else if (shape === 3) {
        const g = svg("g", { transform: `translate(${cxPx} ${cyPx}) rotate(${rotate})`, opacity: "0.4" });
        const r = ringR;
        g.appendChild(svg("polygon", {
            points: `0,-${r * 0.7} ${r * 0.6},${r * 0.35} -${r * 0.6},${r * 0.35}`,
            fill: "none", stroke: p.accent, "stroke-width": "0.7",
        }));
        root.appendChild(g);
    } else {
        const g = svg("g", { opacity: "0.35" });
        for (let i = 0; i < 7; i++) {
            g.appendChild(svg("line", {
                x1: 60 + i * 45, y1: 120, x2: 60 + i * 45, y2: 480,
                stroke: p.accent, "stroke-width": i % 3 === 0 ? "0.9" : "0.4",
            }));
        }
        root.appendChild(g);
    }

    root.append(
        svg("line", { x1: 20, y1: 20, x2: 36, y2: 20, stroke: p.accent, "stroke-opacity": "0.35", "stroke-width": "0.6" }),
        svg("line", { x1: 20, y1: 20, x2: 20, y2: 36, stroke: p.accent, "stroke-opacity": "0.35", "stroke-width": "0.6" }),
        svg("line", { x1: 380, y1: 580, x2: 364, y2: 580, stroke: p.accent, "stroke-opacity": "0.35", "stroke-width": "0.6" }),
        svg("line", { x1: 380, y1: 580, x2: 380, y2: 564, stroke: p.accent, "stroke-opacity": "0.35", "stroke-width": "0.6" }),
    );

    return root;
}

export function pickTone(seed: string): FEPlateTone {
    const tones: FEPlateTone[] = ["nero", "lauro", "oro", "travertino"];
    return tones[feHash(seed) % tones.length] ?? "nero";
}
